const BASES = ['tshirt', 'hoodie', 'tote'];
const FINISHES = ['dtf', 'embroidery'];
const FONTS = {modern: {family: 'Arial', weight: 400}, classic: {family: 'Georgia', weight: 400}, bold: {family: 'Arial', weight: 700}};
const MAX_BYTES = 10 * 1024 * 1024;

export function chooseTechniqueVariant(variants, currentId, technique, techniqueIndex) {
  const candidates = variants.filter(variant => variant.technique === technique);
  if (!candidates.length) return null;
  const current = variants.find(variant => variant.id === currentId);
  return candidates.find(variant => current && variant.options.every((value,index) => index === techniqueIndex || value === current.options[index])) || candidates[0];
}

export function containImage(width, height, size = 1000) {
  const factor = Math.min(size / width, size / height);
  const w = width * factor;
  const h = height * factor;
  return {x: (size - w) / 2, y: (size - h) / 2, width: w, height: h};
}

export function artworkLayout(basis, position, scale, logoAspect = 1, hasLogo = false, hasText = false) {
  const kind = BASES.includes(basis) ? basis : 'tshirt';
  const upper = position === 'upper_left';
  const factor = Math.min(130, Math.max(70, Number(scale) || 100)) / 100;
  const centerX = upper ? .38 : .5;
  const centerY = upper ? {tshirt: .35, hoodie: .43, tote: .53}[kind] : {tshirt: .49, hoodie: .53, tote: .65}[kind];
  const width = (upper ? .20 : .36) * factor;
  const aspect = Number.isFinite(logoAspect) && logoAspect > 0 ? logoAspect : 1;
  const logoHeight = hasLogo ? Math.min(width / aspect, (upper ? .13 : .21) * factor) : 0;
  const logoWidth = logoHeight * aspect;
  const fontSize = (upper ? .028 : .047) * factor;
  const textHeight = hasText ? fontSize * 1.2 : 0;
  const gap = hasLogo && hasText ? .018 * factor : 0;
  const top = centerY - (logoHeight + gap + textHeight) / 2;
  return {centerX, width, fontSize, logo: {x: centerX - logoWidth / 2, y: top, width: logoWidth, height: logoHeight}, textY: top + logoHeight + gap + textHeight / 2};
}

export function checkArtworkFile(type, size, bytes) {
  if (size > MAX_BYTES) return 'file_size_error';
  const b = new Uint8Array(bytes);
  const png = b.length >= 8 && [137,80,78,71,13,10,26,10].every((n,i) => b[i] === n);
  const jpg = b.length >= 3 && b[0] === 255 && b[1] === 216 && b[2] === 255;
  const webp = b.length >= 12 && String.fromCharCode(...b.slice(0,4)) === 'RIFF' && String.fromCharCode(...b.slice(8,12)) === 'WEBP';
  if (!(type === 'image/png' && png || type === 'image/jpeg' && jpg || type === 'image/webp' && webp)) return 'file_type_error';
  return '';
}

if (typeof document !== 'undefined') {
  const instances = new WeakMap();
  const setup = root => {
    if (instances.has(root)) return;
    const config = JSON.parse(root.querySelector('[data-custom-config]').textContent);
    const t = key => config.strings[key] || key;
    const find = selector => root.querySelector(selector);
    const canvas = find('[data-custom-canvas]');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const baseImage = find('[data-custom-base-image]');
    const fileInput = find('[data-custom-file]');
    const textInput = find('[data-custom-text]');
    const fontSelect = find('[data-custom-font]');
    const positionSelect = find('[data-custom-position]');
    const scaleInput = find('[data-custom-scale]');
    const downloadButton = find('[data-custom-download]');
    const errorElement = find('[data-custom-error]');
    const status = find('[data-custom-status]');
    let logoImage = null;
    let logoUrl = '';
    let uploadVersion = 0;
    let busy = false;
    let uploading = false;
    let frame = 0;
    const params = new URL(location.href).searchParams;
    const requestedFinish = params.get('afwerking');
    const fixedBasis = BASES.includes(config.basis) ? config.basis : 'tshirt';
    const fixedImage = config.images[fixedBasis];
    let currentImageSource = fixedImage.src;
    const productForm = find('[data-custom-buy] form');
    const variantSelect = productForm?.querySelector('[data-studio-variant]');
    const variants = variantSelect ? Array.from(variantSelect.options).map(option => ({id: option.value, technique: option.dataset.technique, options: JSON.parse(option.dataset.customOptions)})) : [];
    if (variantSelect?.selectedOptions[0].dataset.technique) find('[data-custom-finish="' + variantSelect.selectedOptions[0].dataset.technique + '"]').checked = true;
    else if (!config.realProduct && FINISHES.includes(requestedFinish)) find('[data-custom-finish="' + requestedFinish + '"]').checked = true;
    if (fixedBasis === 'tote') find('[data-custom-color][value="#22221f"]').checked = true;
    if (config.realProduct && config.techniqueValid) root.querySelectorAll('[data-custom-finish]').forEach(input => input.disabled = !variants.some(variant => variant.technique === input.dataset.customFinish));
    if (params.has('basis')) {const url = new URL(location.href);url.searchParams.delete('basis');history.replaceState(null,'',url.pathname + url.search + url.hash);}

    const state = () => ({
      basis: fixedBasis,
      finish: find('[data-custom-finish]:checked').dataset.customFinish,
      text: textInput.value.trim(),
      font: fontSelect.selectedOptions[0].dataset.font,
      color: find('[data-custom-color]:checked').value,
      position: positionSelect.selectedOptions[0].dataset.position,
      scale: Number(scaleInput.value),
      hasLogo: !!logoImage
    });

    const drawArtwork = (context, data, logo, placeholder = false) => {
      const text = data.text || (placeholder && !logo ? t('placeholder') : '');
      const layout = artworkLayout(data.basis, data.position, data.scale, logo ? logo.naturalWidth / logo.naturalHeight : 1, !!logo, !!text);
      context.save();
      context.clearRect(0,0,1000,1000);
      if (logo) {
        const r = layout.logo;
        context.drawImage(logo, r.x * 1000, r.y * 1000, r.width * 1000, r.height * 1000);
      }
      if (text) {
        const font = FONTS[data.font] || FONTS.modern;
        let size = layout.fontSize * 1000;
        context.font = font.weight + ' ' + size + 'px ' + font.family;
        const measured = context.measureText(text).width;
        if (measured > layout.width * 1000) size *= layout.width * 1000 / measured;
        context.font = font.weight + ' ' + size + 'px ' + font.family;
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillStyle = data.color;
        context.globalAlpha = !data.text && placeholder ? .46 : 1;
        if (data.finish === 'embroidery' && data.text) {
          context.shadowColor = data.color === '#22221f' ? '#0008' : '#ffffff80';
          context.shadowOffsetY = 1;
          context.shadowBlur = .8;
        }
        context.fillText(text, layout.centerX * 1000, layout.textY * 1000);
      }
      context.restore();
    };

    const showError = key => {errorElement.textContent = key ? t(key) : ''; errorElement.hidden = !key;};
    const syncPurchase = () => {
      const data = state();
      const hasArtwork = data.hasLogo || !!data.text;
      const form = productForm;
      root.querySelectorAll('[data-custom-property]').forEach(input => {
        if (form) input.setAttribute('form', form.id); else input.removeAttribute('form');
      });
      const techniqueMatches = !!form && config.techniqueValid && variantSelect.selectedOptions[0].dataset.technique === data.finish;
      find('[data-custom-no-product]').hidden = techniqueMatches;
      const submit = form?.querySelector('[data-product-submit]');
      if (submit) submit.disabled = !hasArtwork || uploading || !techniqueMatches || variantSelect.selectedOptions[0].dataset.available !== 'true';
    };

    const render = () => {
      const data = state();
      const image = config.images[data.basis];
      drawArtwork(ctx, data, logoImage, true);
      find('[data-custom-technique-note]').textContent = t(data.finish + '_note');
      find('[data-custom-summary="basis"]').textContent = image.label;
      find('[data-custom-summary="finish"]').textContent = t(data.finish);
      find('[data-custom-summary="position"]').textContent = t(data.position);
      const content = data.hasLogo ? data.text ? 'content_both' : 'content_logo' : data.text ? 'content_text' : 'content_empty';
      find('[data-custom-summary="content"]').textContent = t(content);
      find('[data-custom-preview-description]').textContent = image.label + ' / ' + t(data.finish) + ' / ' + t(data.position);
      find('[data-custom-scale-output]').textContent = data.scale + '%';
      scaleInput.setAttribute('aria-valuetext', data.scale + '%');
      const hasArtwork = data.hasLogo || !!data.text;
      downloadButton.disabled = !hasArtwork || busy || uploading || !currentImageSource;
      find('[data-custom-download-help]').textContent = t(hasArtwork ? 'download_ready' : 'download_help');
      syncPurchase();
    };
    const scheduleRender = () => {if (!frame) frame = requestAnimationFrame(() => {frame = 0; render();});};
    const clearLogo = () => {
      uploadVersion++;
      if (logoUrl) URL.revokeObjectURL(logoUrl);
      logoUrl = '';
      logoImage = null;
      uploading = false;
      fileInput.value = '';
      find('[data-custom-file-state]').hidden = true;
    };

    const readLogo = async () => {
      const file = fileInput.files?.[0];
      if (!file) return;
      const version = ++uploadVersion;
      uploading = true;
      if (logoUrl) URL.revokeObjectURL(logoUrl);
      logoUrl = '';
      logoImage = null;
      find('[data-custom-file-state]').hidden = true;
      status.textContent = '';
      showError('');
      render();
      try {
        const signature = await file.slice(0,12).arrayBuffer();
        if (version !== uploadVersion) return;
        const invalid = checkArtworkFile(file.type, file.size, signature);
        if (invalid) {clearLogo();showError(invalid);render();return;}
        const nextUrl = URL.createObjectURL(file);
        const image = new Image();
        image.src = nextUrl;
        try {await image.decode();} catch {URL.revokeObjectURL(nextUrl);throw new Error('file_decode_error');}
        if (version !== uploadVersion) {URL.revokeObjectURL(nextUrl);return;}
        if (image.naturalWidth * image.naturalHeight > 25000000) {URL.revokeObjectURL(nextUrl);clearLogo();showError('file_dimensions_error');render();return;}
        if (logoUrl) URL.revokeObjectURL(logoUrl);
        logoUrl = nextUrl;
        logoImage = image;
        find('[data-custom-file-name]').textContent = file.name;
        find('[data-custom-file-state]').hidden = false;
        status.textContent = t('uploaded');
        render();
      } catch {
        if (version !== uploadVersion) return;
        clearLogo();showError('file_decode_error');render();
      } finally {if (version === uploadVersion) {uploading = false;render();}}
    };

    root.addEventListener('input', event => {
      if (event.target.matches('[data-custom-text],[data-custom-scale]')) {showError('');scheduleRender();}
    });
    root.addEventListener('change', event => {
      const target = event.target;
      if (target.matches('[data-custom-file]')) {readLogo();return;}
      if (target.matches('[data-custom-finish]')) {
        if (variantSelect && config.techniqueValid) {
          const next = chooseTechniqueVariant(variants,variantSelect.value,target.dataset.customFinish,config.techniqueIndex);
          if (next) {
            variantSelect.value = next.id;
            variantSelect.dispatchEvent(new Event('change',{bubbles:true}));
            status.textContent = t('variant_changed') + ' ' + variantSelect.selectedOptions[0].textContent.trim();
          } else showError('method_unavailable');
        }
        const url = new URL(location.href);
        url.searchParams.set('afwerking', target.dataset.customFinish);
        history.replaceState(null, '', url.pathname + url.search + url.hash);
      }
      if (target.matches('[data-studio-variant]')) {
        const option = target.selectedOptions[0];
        if (option.dataset.technique) find('[data-custom-finish="' + option.dataset.technique + '"]').checked = true;
        const nextImage = option.dataset.image || config.defaultImage || '';
        if (nextImage && nextImage !== currentImageSource) {baseImage.removeAttribute('srcset');baseImage.src = nextImage;}
        baseImage.hidden = !nextImage;
        const missingImage = find('.skr-custom-image-missing');
        if (missingImage) missingImage.hidden = !!nextImage;
        currentImageSource = nextImage;
        queueMicrotask(render);
      }
      else if (!target.matches('[data-custom-file]')) render();
    });
    root.addEventListener('submit', event => {
      const data = state();
      if (uploading) {event.preventDefault();showError('upload_wait');return;}
      if (!config.techniqueValid || variantSelect?.selectedOptions[0].dataset.technique !== data.finish) {event.preventDefault();showError('method_unavailable');return;}
      if (!data.hasLogo && !data.text) {event.preventDefault();showError('empty_error');textInput.focus();}
    });
    find('[data-custom-remove]').addEventListener('click', () => {clearLogo();showError('');status.textContent = t('removed');render();});
    downloadButton.addEventListener('click', async () => {
      const data = state();
      const selectedLogo = logoImage;
      const selectedImageSource = currentImageSource;
      if (!data.hasLogo && !data.text || busy || uploading) return;
      busy = true;showError('');render();
      try {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.src = selectedImageSource;
        await image.decode();
        const output = document.createElement('canvas');
        output.width = 1000;output.height = 1100;
        const context = output.getContext('2d');
        context.fillStyle = '#eae5db';context.fillRect(0,0,1000,1100);
        const imageRect = containImage(image.naturalWidth,image.naturalHeight);
        context.drawImage(image,imageRect.x,imageRect.y,imageRect.width,imageRect.height);
        const artwork = document.createElement('canvas');artwork.width = artwork.height = 1000;
        drawArtwork(artwork.getContext('2d'),data,selectedLogo);
        context.drawImage(artwork,0,0);
        context.fillStyle = '#22221f';context.font = '22px Arial';
        context.fillText(config.images[data.basis].label + ' / ' + t(data.finish) + ' / ' + t(data.position),35,1035);
        context.fillStyle = '#727067';context.font = '16px Arial';context.fillText(t('export_note'),35,1072);
        const blob = await new Promise(resolve => output.toBlob(resolve,'image/png'));
        if (!blob) throw new Error('empty');
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');link.href = url;link.download = 'samen-kr-' + data.basis + '-' + data.finish + '-ontwerp.png';
        document.body.append(link);link.click();link.remove();
        setTimeout(() => URL.revokeObjectURL(url),2000);
        status.textContent = t('download_success');
      } catch {showError('download_error');}
      finally {busy = false;render();}
    });
    instances.set(root, {destroy: () => {clearLogo();if (frame) cancelAnimationFrame(frame);}});
    render();
  };
  document.querySelectorAll('[data-studio-customizer]').forEach(setup);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-studio-customizer]').forEach(setup));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-studio-customizer]').forEach(root => instances.get(root)?.destroy()));
}
